import { showDeleteConfirmation, showYesNo } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import {
  CompactProposalTableResponse,
  ProposalActivitiesPageQuery,
  ProposalId,
  ProposalResponse,
  ProposalStatus,
  ProposalsPageQuery,
} from '@business/entities/proposal';
import { ProposalAPI } from '@business/api/proposal_api';
import { AddProposalFormData } from '@modules/proposals/AddProposalModal/AddProposalModal';
import { ProposalFormData } from '@modules/proposal/ProposalBuilderPage';
import { OrgId } from '@business/entities/organization';
import { LineItemFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { R } from '@core/utils/r';
import { EventMeta } from '@business/entities/calendar';
import { TMBoardId } from '@business/entities/tm/TimeMaterialBoardResponse';
import { of, Subject } from 'rxjs';
import { openMultipleDealLostModal } from '@modules/deals/DealOverlay/MultipleDealLostModal';
import { DealStatus } from '@business/entities/deal';

export class ProposalBloc extends BaseBloc {
  private readonly proposalsRepo = new Repository<ProposalResponse | CompactProposalTableResponse>({
    getItemId: proposal => proposal.id,
  });

  readonly proposals$ = this.proposalsRepo.items$;
  readonly selectproposal = this.proposalsRepo.selectItem;

  onReset() {
    this.proposalsRepo.reset();
  }

  fetchProposals = (data?: ProposalsPageQuery) => {
    return ProposalAPI.fetchProposals(data).pipe(this.proposalsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_proposals') }));
  };

  fetchProposalsPaginated = (data?: ProposalsPageQuery) => {
    return ProposalAPI.fetchProposalsPaginated(data).pipe(handleMessage({ error: requestMessage('fetch_proposals') }));
  };

  fetchProposalActivities = (data?: ProposalActivitiesPageQuery) => {
    return ProposalAPI.fetchProposalActivities(data).pipe(handleMessage({ error: requestMessage('fetch_proposals') }));
  };

  fetchProposal = (id: ProposalId) => {
    return ProposalAPI.fetchProposal(id).pipe(
      this.proposalsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_proposals') }),
    );
  };

  createProposal = (data: AddProposalFormData, meta?: EventMeta, autoSave = false) => {
    return ProposalAPI.createProposal(data, meta, autoSave).pipe(
      this.proposalsRepo.ops.addOne(item => item),
      //   !autoSave
      //     ? handleMessage({
      //         type: requestMessage('create_proposal'),
      //       })
      //     : map(proposal => {
      //         return proposal;
      //       }),
    );
  };

  updateProposal = (
    id: ProposalId,
    data: Partial<ProposalResponse>,
    isPublic?: boolean,
    selectedProducts?: LineItemFormData[],
    autoSave = false,
    isEditing = false,
  ) => {
    return ProposalAPI.updateProposal(id, data, isPublic, selectedProducts, autoSave, isEditing).pipe(
      this.proposalsRepo.ops.upsertOne(item => ({ item })),
      !autoSave
        ? handleMessage({
            type: requestMessage('update_proposal'),
          })
        : map(proposal => {
            return proposal;
          }),
    );
  };

  duplicateProposal = (
    id: ProposalId,
    data: Partial<ProposalResponse>,
    isPublic?: boolean,
    selectedProducts?: LineItemFormData[],
    autoSave = false,
  ) => {
    return ProposalAPI.duplicateProposal(id, data, isPublic, selectedProducts, autoSave).pipe(
      this.proposalsRepo.ops.upsertOne(item => ({ item })),
      !autoSave
        ? handleMessage({
            type: requestMessage('duplicate_proposal'),
          })
        : map(proposal => {
            return proposal;
          }),
    );
  };

  newDuplicateProposal = (id: ProposalId) => {
    return ProposalAPI.newDuplicateProposal(id).pipe(
      this.proposalsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('duplicate_proposal'),
      }),
    );
  };

  updateStatus = (id: ProposalId, data: { status: ProposalStatus }, isPublic?: boolean) => {
    return ProposalAPI.updateStatus(id, data, isPublic).pipe(
      this.proposalsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_proposal'),
      }),
    );
  };

  printProposal = (id: ProposalId) => {
    return ProposalAPI.printProposal(id).pipe();
  };

  updateTitle = (id: ProposalId, title: string) => {
    return ProposalAPI.updateTitle(id, title).pipe(
      this.proposalsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_proposal'),
      }),
    );
  };

  deleteProposal = (id: ProposalId) => {
    let dealIds: string[] = [];
    return showDeleteConfirmation('Archive Proposal', `Do you really want to archive this proposal?`).pipe(
      switchMap(() => ProposalAPI.deleteProposal(id)),
      switchMap(result => {
        if (result.openDeals.length > 0) {
          dealIds = result.openDeals;
          return showYesNo(
            'Archive Deals',
            `There are ${result.openDeals.length} open deals associated with this proposal. Do you want to mark them lost as well?`,
          );
        } else {
          return of(false);
        }
      }),
      switchMap(result => {
        if (!result) return of(true);
        return openMultipleDealLostModal({
          dealIds,
          status: DealStatus.lost,
        });
      }),

      this.proposalsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_proposal'),
      }),
    );
  };

  resendProposal = (id: ProposalId, data: any) => {
    return ProposalAPI.resendProposal(id, data).pipe(
      handleMessage({
        type: requestMessage('resend_proposal'),
      }),
    );
  };

  getProposalResendInfo = (id: ProposalId) => {
    return ProposalAPI.getProposalResendInfo(id);
  };

  createWorkOrder = (id: ProposalId, boardId: TMBoardId) => {
    return ProposalAPI.createWorkOrder(id, boardId).pipe(
      handleMessage({
        type: requestMessage('create_work_order'),
      }),
    );
  };

  unarchiveProposal = (id: ProposalId) => {
    return ProposalAPI.unarchiveProposal(id).pipe(
      handleMessage({
        type: requestMessage('unarchive_proposal'),
      }),
    );
  };

  unsetProposal = (id: ProposalId) => {
    this.proposalsRepo.removeOne(id);
  };

  hardDeleteProposal = (id: ProposalId) => {
    let dealIds: string[] = [];
    return showDeleteConfirmation('Delete Proposal', `Do you really want to delete this proposal?`).pipe(
      switchMap(() => ProposalAPI.hardDeleteProposal(id)),
      switchMap(result => {
        if (result.openDeals.length > 0) {
          dealIds = result.openDeals;
          return showYesNo(
            'Archive Deals',
            `There are ${result.openDeals.length} open deals associated with this proposal. Do you want to mark them lost as well?`,
          );
        } else {
          return of(false);
        }
      }),
      switchMap(result => {
        if (!result) return of(true);
        return openMultipleDealLostModal({
          dealIds,
          status: DealStatus.lost,
        });
      }),

      this.proposalsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('hard_delete_proposal'),
      }),
    );
  };
}
